import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import { getTranslateByKey } from '@/main/services/utils';
import { withQueryparams } from '@/main/components/core/queryParams/queryparams';
import { templateEmailNotificationApi, templateEmailNotificationVariableApi } from '@/main/api';
import {
  TemplateEmailNotificationDetailResponse,
  TemplateEmailNotificationDetailVariableResponse,
  TemplateEmailNotificationVariableResponse
} from '@/main/models/response/templateEmailNotificationResponse';
import { ICultureData } from './sectionEmailTemplateBody/sectionEmailTemplateBody';

import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import SectionEmailTemplateData, { ISectionEmailTemplateDataProps } from './sectionEmailTemplateData/sectionEmailTemplateData';
import SectionEmailTemplateBody, { ISectionEmailTemplateBodyHandle } from './sectionEmailTemplateBody/sectionEmailTemplateBody';
import SectionEmailTemplateHistorical from './sectionEmailTemplateHistorical/sectionEmailTemplateHistorical';
import * as S from './styles';

function getCultureDisplayName(idCulture: string): string {
  const code = idCulture.toLowerCase();
  if (code.startsWith('en')) return getTranslateByKey('culture.unitedStates');
  if (code.startsWith('es')) return getTranslateByKey('culture.mexico');
  return idCulture.toUpperCase();
}

function RegisterEmailTemplateScreen() {
  const titlePage = `${getTranslateByKey(
    'information.registration'
  )} > ${getTranslateByKey('screen.EmailTemplates')}`;

  const navigate = useNavigate();
  const bodyRef = useRef<ISectionEmailTemplateBodyHandle>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [historicalRefreshKey, setHistoricalRefreshKey] = useState(0);
  const [searchParams] = useSearchParams();
  const guidCompany = searchParams.get('guidCompany') ?? '';
  const idTemplateEmailNotification = Number(searchParams.get('idTemplateEmailNotification'));
  const idSystemApplication = Number(searchParams.get('idSystemApplication'));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [cultures, setCultures] = useState<ICultureData[]>([]);
  const [variablesDefault, setVariablesDefault] = useState<TemplateEmailNotificationDetailVariableResponse[]>([]);
  const [availableVariables, setAvailableVariables] = useState<TemplateEmailNotificationVariableResponse[]>([]);
  const [templateData, setTemplateData] = useState<ISectionEmailTemplateDataProps>({});

  useEffect(() => {
    if (!guidCompany || !idTemplateEmailNotification || !idSystemApplication) return;

    setIsLoading(true);
    setHasError(false);
    Promise.all([
      templateEmailNotificationApi.getDetail({ companyGuid: guidCompany, idTemplateEmailNotification, idSystemApplication }),
      templateEmailNotificationVariableApi.getVariables({ idTemplateEmailNotification })
    ])
      .then(([detailRes, variablesRes]) => {
        const detail = detailRes.data.result;

        const mappedCultures: ICultureData[] = (detail.templateEmailNotificationDetails ?? []).map((d: TemplateEmailNotificationDetailResponse) => ({
          culture: d.idCulture,
          cultureName: getCultureDisplayName(d.idCulture),
          title: d.subject,
          body: d.body,
          templateEmailNotificationCompanyGuid: d.templateEmailNotificationCompanyGuid,
          primaryColor: d.primaryColor
        }));

        setCultures(mappedCultures);
        setVariablesDefault(detail.templateEmailNotificationVariables ?? []);
        setAvailableVariables(variablesRes.data.result ?? []);
        setTemplateData({
          country: detail.countryGuid
            ? { key: detail.countryGuid, value: detail.countryName }
            : null,
          insurer: detail.insurerGuid
            ? { key: detail.insurerGuid, value: detail.insurerName }
            : null,
          systemApplication: detail.idSystemApplication
            ? { key: detail.idSystemApplication, value: detail.systemApplicationName }
            : null,
          templateType: detail.idTemplateEmailNotification
            ? { key: detail.idTemplateEmailNotification, value: detail.templateEmailNotificationName }
            : null
        });
      })
      .catch(() => setHasError(true))
      .finally(() => setIsLoading(false));
  }, [guidCompany, idTemplateEmailNotification, idSystemApplication]);

  async function handleSave(dirtyCultures: ICultureData[]): Promise<void> {
    const results = await Promise.allSettled(
      dirtyCultures.map(c =>
        templateEmailNotificationApi.update(
          {
            templateEmailNotificationCompanyGuid: c.templateEmailNotificationCompanyGuid,
            subject: c.title,
            body: c.body
          },
          { suppressToast: true }
        )
      )
    );

    let hasSaveError = false;
    results.forEach((result, index) => {
      const cultureName = dirtyCultures[index].cultureName;
      if (result.status === 'fulfilled') {
        const errorMessages: Array<{ value: string }> = result.value?.data?.errorMessages ?? [];
        if (errorMessages.length > 0) {
          hasSaveError = true;
          errorMessages.forEach(e => toast.error(`${cultureName} - ${e.value}`));
        } else {
          toast.success(`${cultureName} - ${getTranslateByKey('information.savedSuccess')}`);
        }
      } else {
        hasSaveError = true;
        const axiosError = result.reason as AxiosError<{ errorMessages?: Array<{ value: string }> }>;
        const serverMessages = axiosError?.response?.data?.errorMessages;
        if (serverMessages && serverMessages.length > 0) {
          serverMessages.forEach(e => toast.error(`${cultureName} - ${e.value}`));
        } else {
          toast.error(`${cultureName} - ${getTranslateByKey('information.anUnexpectedErrorOccurredPleaseTryAgainLater', true)}`);
        }
      }
    });

    if (hasSaveError) throw new Error('some cultures failed to save');
  }

  async function handleSaveClick() {
    if (!bodyRef.current) return;
    setIsSaving(true);
    try {
      await bodyRef.current.triggerSave();
      setHistoricalRefreshKey(k => k + 1);
    } catch {
      // per-culture error toasts already shown in handleSave
    } finally {
      setIsSaving(false);
    }
  }

  if (hasError) {
    return (
      <ContainerScreen title={titlePage}>
        <ResultNotFoundComponent withButtonBack messageCustom={getTranslateByKey('information.anUnexpectedErrorOccurredPleaseTryAgainLater', true)} />
      </ContainerScreen>
    );
  }

  return (
    <ContainerScreen title={titlePage}>
      <S.Container>
        <SectionEmailTemplateData
          country={templateData.country}
          insurer={templateData.insurer}
          systemApplication={templateData.systemApplication}
          templateType={templateData.templateType}
        />
        <SectionEmailTemplateBody
          ref={bodyRef}
          cultures={cultures}
          variables={availableVariables}
          variablesDefault={variablesDefault}
          isLoading={isLoading}
          onSave={handleSave}
        />
        <SectionEmailTemplateHistorical
          guidCompany={guidCompany}
          idTemplateEmailNotification={idTemplateEmailNotification}
          idSystemApplication={idSystemApplication}
          refreshKey={historicalRefreshKey}
          defaultVariableNames={variablesDefault.filter(v => v.isDefault).map(v => v.variable)}
        />
        <S.ActionRow>
          <SecondaryButton
            id="id-registerEmailTemplate-btn-back"
            onClick={() => navigate(-1)}
          >
            {getTranslateByKey('information.back')}
          </SecondaryButton>
          <Button
            id="id-registerEmailTemplate-btn-save"
            isLoading={isSaving}
            onClick={handleSaveClick}
          >
            {getTranslateByKey('information.save')}
          </Button>
        </S.ActionRow>
      </S.Container>
    </ContainerScreen>
  );
}

export default withQueryparams(RegisterEmailTemplateScreen);
